import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { pickLocalized } from './RichContentModal';
import { SOCIAL_ICON_MAP } from './SocialLinksManagement';
import { Link as LinkIcon } from '@phosphor-icons/react';
import { useBranding } from '../context/BrandingContext';
import { InstallPWA } from './InstallPWA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const platformName = branding?.platform_name || 'ATÔ';
  const [sections, setSections] = useState([]);
  const [socials, setSocials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/footer`).then(res => setSections(res.data || [])).catch(() => {});
    axios.get(`${API}/social-links`).then(res => setSocials(res.data || [])).catch(() => {});
  }, []);

  const lang = (i18n.language || 'fr').split('-')[0];

  // Iter 83 — Footer is navigation-only. No modal branch, no per-link
  // content. Legal / support text lives in `content_pages` and is
  // rendered by `<Route path="/pages/:slug">`.
  const renderLink = (link) => {
    const label = pickLocalized(link.title_i18n, lang);
    const href = link.url;
    const testId = `footer-link-${(href || '').replace(/^\//, '').replace(/\//g, '-') || link.link_id}`;

    if (link.external || (href || '').startsWith('http')) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-text_muted hover:text-primary transition-colors"
          data-testid={testId}
        >
          {label}
        </a>
      );
    }
    return (
      <Link
        to={href}
        className="text-sm text-text_muted hover:text-primary transition-colors"
        data-testid={testId}
      >
        {label}
      </Link>
    );
  };

  return (
    <footer className="bg-white border-t border-border py-10 mt-12" data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6">
        {/* Top: Brand + Link Sections */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-3 md:col-span-2">
            <p
              className="text-xl font-black font-heading text-text_strong leading-none mb-2"
              data-testid="footer-wordmark"
            >
              {platformName}
            </p>
            <p className="text-sm text-text_muted mb-4 max-w-xs">{(branding?.taglines?.[lang] || '').trim() || t('home.tagline')}</p>
          </div>

          {/* Dynamic Link Sections */}
          {sections.map((section) => (
            <div key={section.section_id} data-testid={`footer-section-${section.key}`}>
              <h4 className="font-semibold text-text_main text-sm mb-3">
                {pickLocalized(section.title_i18n, lang)}
              </h4>
              <ul className="space-y-2">
                {(section.links || []).map((link) => (
                  <li key={link.link_id}>{renderLink(link)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Middle: App Download + Social */}
        <div className="border-t border-border pt-8 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div data-testid="footer-app-download">
              <InstallPWA />
            </div>

            <div data-testid="footer-social-links">
              <p className="font-semibold text-text_main text-sm mb-3">{t('footer.followUs')}</p>
              <div className="flex items-center gap-4 flex-wrap">
                {socials.map((s) => {
                  const Icon = SOCIAL_ICON_MAP[s.icon] || LinkIcon;
                  return (
                    <a
                      key={s.item_id}
                      href={s.href}
                      target={s.type === 'link' ? '_blank' : undefined}
                      rel={s.type === 'link' ? 'noopener noreferrer' : undefined}
                      title={s.name}
                      aria-label={s.name}
                      className="text-text_muted hover:text-primary transition-colors"
                      data-testid={`social-${s.icon}-${s.item_id}`}
                    >
                      <Icon size={24} weight="regular" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-border pt-6 text-center">
          <p className="text-sm text-text_muted" data-testid="footer-copyright">&copy; 2026 {platformName} Marketplace</p>
        </div>
      </div>
    </footer>
  );
};
